import { Box, Typography, Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import stripesImg from "./imgs/stripes.jpg";
import { DEFAULT_HOME_HERO } from "./usePublicSiteSettings";
import { normalizeQuotePageTemplate } from "./quotePageTemplates";

/**
 * Hero region for public quote page; same copy and hero image URL as today, different visual shells per template.
 * @param {boolean} [compact] — fixed-height layout for dashboard preview (operators).
 */
export default function QuoteTemplateHero({
  templateId = "classic",
  homeHero,
  primaryColor = "#2d6a4f",
  accentColor = "#4caf50",
  operatorDisplayName = "",
  compact = false,
}) {
  const template = normalizeQuotePageTemplate(templateId);
  const hero =
    homeHero && typeof homeHero === "object" ? { ...DEFAULT_HOME_HERO, ...homeHero } : DEFAULT_HOME_HERO;
  const heroBgUrl =
    hero.imageUrl && String(hero.imageUrl).trim() ? String(hero.imageUrl).trim() : stripesImg;

  const showOp = Boolean((operatorDisplayName || "").trim());

  const BrandChip = showOp ? (
    <Chip
      size="small"
      label={(operatorDisplayName || "").trim()}
      sx={{
        mb: compact ? 1 : 2,
        fontWeight: 600,
        letterSpacing: "0.02em",
        backgroundColor: alpha(primaryColor, 0.12),
        color: template === "noir" || template === "sunrise" ? "#fff" : primaryColor,
        border:
          template === "noir"
            ? "1px solid rgba(255,255,255,0.2)"
            : template === "sunrise"
              ? "1px solid rgba(255,255,255,0.35)"
              : `1px solid ${alpha(primaryColor, 0.25)}`,
        ...(template === "noir" && { backgroundColor: "rgba(255,255,255,0.08)" }),
        ...(template === "sunrise" && { backgroundColor: "rgba(255,255,255,0.15)" }),
      }}
    />
  ) : null;

  if (template === "classic") {
    const heroBackgroundImage = `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.45) 100%), url(${heroBgUrl})`;
    return (
      <Box
        className="mow-hero"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: compact ? 220 : { xs: "36vh", sm: "40vh" },
          backgroundImage: heroBackgroundImage,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          pt: compact ? 2.5 : 7,
          pb: compact ? 1.5 : 2,
        }}
      >
        <Box
          sx={{
            borderRadius: 4,
            px: compact ? 2 : 3,
            py: compact ? 1.75 : 2.5,
            maxWidth: 440,
            textAlign: "center",
            background: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            border: "1px solid rgba(255,255,255,0.85)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
          }}
        >
          {BrandChip}
          <Typography
            variant="h5"
            sx={{
              color: "text.primary",
              fontWeight: 800,
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
              fontSize: compact ? "1.05rem" : undefined,
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.headline}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: compact ? 1 : 1.5,
              color: "text.secondary",
              lineHeight: 1.55,
              fontSize: compact ? "0.8rem" : undefined,
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.subheadline}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (template === "meadow") {
    return (
      <Box
        className="mow-hero"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: compact ? 228 : { xs: "42vh", sm: "46vh" },
          overflow: "hidden",
          background: `linear-gradient(165deg, ${alpha(primaryColor, 0.14)} 0%, #f6faf7 38%, #eef5ef 100%)`,
          "&::before": {
            content: '""',
            position: "absolute",
            width: "120%",
            height: "70%",
            top: "-20%",
            left: "-10%",
            background: `radial-gradient(ellipse at 30% 20%, ${alpha(accentColor, 0.22)} 0%, transparent 55%)`,
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: "80%",
            height: "50%",
            bottom: "-10%",
            right: "-15%",
            background: `radial-gradient(ellipse at 70% 80%, ${alpha(primaryColor, 0.12)} 0%, transparent 50%)`,
            pointerEvents: "none",
          },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          pt: compact ? 2 : 6,
          pb: compact ? 2 : 3,
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 520,
            width: "100%",
            textAlign: "center",
            borderRadius: 5,
            py: compact ? 2 : 3.5,
            px: compact ? 2 : 3,
            background: "rgba(255,255,255,0.85)",
            border: `1px solid ${alpha(primaryColor, 0.18)}`,
            boxShadow: `0 24px 64px ${alpha(primaryColor, 0.12)}, 0 0 0 1px rgba(255,255,255,0.8) inset`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: primaryColor,
              fontWeight: 700,
              letterSpacing: compact ? "0.2em" : "0.28em",
              display: "block",
              mb: compact ? 0.5 : 1,
              fontSize: compact ? "0.65rem" : undefined,
            }}
          >
            Instant quote
          </Typography>
          {BrandChip}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1.12,
              color: "#1a2e22",
              fontSize: compact ? "1.15rem" : { xs: "1.65rem", sm: "2rem" },
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.headline}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: compact ? 1 : 2,
              color: "rgba(26,46,34,0.72)",
              lineHeight: 1.65,
              fontSize: compact ? "0.82rem" : "1rem",
              maxWidth: 440,
              mx: "auto",
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.subheadline}
          </Typography>
          <Box
            sx={{
              mt: compact ? 1.5 : 2.5,
              height: 4,
              width: 72,
              mx: "auto",
              borderRadius: 2,
              background: `linear-gradient(90deg, ${accentColor}, ${primaryColor})`,
            }}
          />
        </Box>
      </Box>
    );
  }

  if (template === "noir") {
    return (
      <Box
        className="mow-hero"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: compact ? 228 : { xs: "40vh", sm: "44vh" },
          background: "linear-gradient(180deg, #0a0a0c 0%, #141418 50%, #0d0d10 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          pt: compact ? 2 : 6,
          pb: compact ? 2 : 3,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(255,255,255,0.06) 0%, transparent 60%)",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 500,
            width: "100%",
            textAlign: "left",
            borderRadius: 3,
            py: compact ? 2 : 3.5,
            px: compact ? 2 : { xs: 2.5, sm: 4 },
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 32px 64px rgba(0,0,0,0.45)",
          }}
        >
          <Box
            sx={{
              width: compact ? 36 : 48,
              height: 3,
              backgroundColor: accentColor,
              borderRadius: 1,
              mb: compact ? 1.5 : 2.5,
            }}
          />
          {BrandChip}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.15,
              color: "#f5f5f5",
              fontSize: compact ? "1.15rem" : { xs: "1.6rem", sm: "2.05rem" },
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.headline}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: compact ? 1 : 2,
              color: "rgba(245,245,245,0.62)",
              lineHeight: 1.65,
              fontWeight: 400,
              fontSize: compact ? "0.82rem" : undefined,
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.subheadline}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (template === "coastal") {
    return (
      <Box
        className="mow-hero"
        sx={{
          position: "relative",
          width: "100%",
          minHeight: compact ? 228 : { xs: "40vh", sm: "44vh" },
          background: `linear-gradient(185deg, #e3f2fd 0%, #fafdff 45%, #ffffff 100%)`,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          pt: compact ? 2 : 5,
          pb: compact ? 1.5 : 2,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: compact ? 48 : 72,
            color: alpha(primaryColor, 0.12),
          }}
        >
          <path
            fill="currentColor"
            d="M0,64 C240,20 480,100 720,50 C960,0 1200,80 1440,40 L1440,120 L0,120 Z"
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: 540,
            width: "100%",
            textAlign: "center",
          }}
        >
          {BrandChip}
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.035em",
              lineHeight: 1.12,
              color: "#0d3d4a",
              fontSize: compact ? "1.15rem" : { xs: "1.65rem", sm: "2.05rem" },
              textShadow: "0 1px 0 rgba(255,255,255,0.8)",
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.headline}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: compact ? 1 : 2,
              color: "rgba(13,61,74,0.75)",
              lineHeight: 1.65,
              maxWidth: 480,
              mx: "auto",
              fontSize: compact ? "0.82rem" : undefined,
              ...(compact
                ? {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }
                : {}),
            }}
          >
            {hero.subheadline}
          </Typography>
          <Box
            sx={{
              mt: compact ? 1.5 : 2.5,
              display: "flex",
              gap: 0.75,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: compact ? 6 : 8,
                  height: compact ? 6 : 8,
                  borderRadius: "50%",
                  backgroundColor: i === 1 ? accentColor : alpha(primaryColor, 0.35),
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  /* sunrise */
  return (
    <Box
      className="mow-hero"
      sx={{
        position: "relative",
        width: "100%",
        minHeight: compact ? 228 : { xs: "42vh", sm: "46vh" },
        background: `linear-gradient(135deg, ${accentColor} 0%, ${primaryColor} 52%, ${alpha(primaryColor, 0.92)} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        pt: compact ? 2 : 6,
        pb: compact ? 2 : 3,
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: "140%",
          height: "60%",
          top: "-25%",
          right: "-25%",
          background: "radial-gradient(circle, rgba(255,255,255,0.35) 0%, transparent 55%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: "80%",
          height: "40%",
          bottom: "-5%",
          left: "-10%",
          background: "radial-gradient(circle, rgba(0,0,0,0.08) 0%, transparent 60%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 560, textAlign: "center" }}>
        {BrandChip}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            letterSpacing: "-0.04em",
            lineHeight: 1.05,
            color: "#fff",
            fontSize: compact ? "1.2rem" : { xs: "1.85rem", sm: "2.5rem" },
            textShadow: "0 4px 24px rgba(0,0,0,0.2)",
            ...(compact
              ? {
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }
              : {}),
          }}
        >
          {hero.headline}
        </Typography>
        <Typography
          variant="h6"
          component="p"
          sx={{
            mt: compact ? 1 : 2,
            fontWeight: 500,
            fontSize: compact ? "0.88rem" : { xs: "1rem", sm: "1.15rem" },
            lineHeight: 1.55,
            color: "rgba(255,255,255,0.92)",
            maxWidth: 500,
            mx: "auto",
            ...(compact
              ? {
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }
              : {}),
          }}
        >
          {hero.subheadline}
        </Typography>
      </Box>
    </Box>
  );
}
